import { useEffect, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, X, Crown, Rocket, Building2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const PLANS = [
  {
    id: "free",
    label: "Free",
    price: "$0",
    subtitle: "Para probar Cotflow",
    icon: Sparkles,
    color: "text-slate-600",
    ring: "border-slate-200",
    features: [
      { text: "3 cotizaciones por mes", included: true },
      { text: "10 clientes máx.", included: true },
      { text: "3 productos/servicios máx.", included: true },
      { text: "1 template propio", included: true },
      { text: "Generación IA de cotizaciones", included: true },
      { text: "Pipeline CRM Kanban", included: false },
      { text: "Análisis IA de clientes", included: false },
      { text: "Imágenes en secciones extra", included: false },
      { text: "Versionado de cotizaciones", included: false },
      { text: "Multi-usuario (Admin / Supervisor / Usuario)", included: false },
    ],
  },
  {
    id: "pro",
    label: "Pro",
    price: "$9.990",
    priceSuffix: "/mes",
    subtitle: "Profesionales individuales",
    icon: Rocket,
    color: "text-blue-600",
    ring: "border-blue-500 ring-2 ring-blue-100",
    highlight: "Más popular",
    features: [
      { text: "Cotizaciones ilimitadas", included: true },
      { text: "Clientes y productos ilimitados", included: true },
      { text: "Templates ilimitados", included: true },
      { text: "Pipeline CRM Kanban completo", included: true },
      { text: "Análisis IA de clientes", included: true },
      { text: "Imágenes y secciones extra enriquecidas", included: true },
      { text: "Versionado de cotizaciones", included: true },
      { text: "Multi-usuario", included: false },
    ],
  },
  {
    id: "empresa",
    label: "Empresa",
    price: "$14.990",
    priceSuffix: "/mes",
    subtitle: "Equipos y estudios",
    icon: Building2,
    color: "text-violet-600",
    ring: "border-violet-500",
    features: [
      { text: "Todo lo de Pro", included: true, bold: true },
      { text: "Multi-usuario (3 incluidos)", included: true },
      { text: "Roles Admin / Supervisor / Usuario", included: true },
      { text: "Reasignación de responsables", included: true },
      { text: "Filtros por rol y creador en Pipeline", included: true },
      { text: "Usuarios adicionales bajo demanda", included: true },
    ],
  },
];

export default function PlanPage() {
  const { user, plan, refreshPlan } = useAuth();
  const [showContact, setShowContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => { refreshPlan(); }, [refreshPlan]);

  const current = plan?.plan || "free";
  const usage = plan?.usage || {};
  const features = plan?.features || {};

  const requestUpgrade = async (planId) => {
    if (planId === current) return;
    setShowContact(planId);
    if (planId !== "free") {
      setEmailStatus("loading");
      try {
        const res = await api.post("/plans/request-upgrade", { plan: planId });
        setEmailStatus(res.data);
      } catch (e) {
        setEmailStatus({ error: e.response?.data?.detail || "No se pudo registrar la solicitud" });
      }
    }
  };

  // Solo para el flujo interno del owner (mientras no hay pagos automatizados):
  const applyPlanManual = async (planId) => {
    setSaving(true);
    try {
      await api.post("/plan/change", { plan: planId, user_quota: planId === "empresa" ? 3 : 1 });
      toast.success(`Plan actualizado a ${planId.toUpperCase()}`);
      setShowContact(null);
      await refreshPlan();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Error al cambiar plan");
    }
    setSaving(false);
  };

  const isOwner = user?.role === "owner";

  return (
    <div className="max-w-6xl space-y-8" data-testid="plan-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900">Mi plan</h1>
        <p className="text-slate-500 text-sm mt-1">
          Tu plan actual es <strong>{plan?.label || "—"}</strong>. Actualiza cuando lo necesites.
        </p>
      </div>

      {/* Uso actual */}
      {plan && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["Cotizaciones este mes", usage.quotations_this_month, features.quotations_per_month],
            ["Clientes", usage.clients, features.clients],
            ["Productos", usage.products, features.products],
            ["Templates propios", usage.templates, features.templates],
          ].map(([label, used, limit]) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">
                {used ?? 0}
                <span className="text-sm font-medium text-slate-400 ml-1">/ {limit ?? "∞"}</span>
              </div>
              {limit != null && used >= limit && (
                <div className="mt-1 text-[10px] text-red-600 font-medium">Límite alcanzado</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Planes */}
      <div className="grid md:grid-cols-3 gap-4">
        {PLANS.map(pl => {
          const Icon = pl.icon;
          const isCurrent = current === pl.id;
          return (
            <div key={pl.id} className={`relative bg-white rounded-2xl border ${pl.ring} p-6 flex flex-col`} data-testid={`plan-card-${pl.id}`}>
              {pl.highlight && (
                <span className="absolute -top-3 left-6 bg-blue-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">{pl.highlight}</span>
              )}
              <Icon size={24} className={pl.color} />
              <h3 className="text-2xl font-black text-slate-900 mt-3">{pl.label}</h3>
              <p className="text-sm text-slate-500">{pl.subtitle}</p>
              <div className="mt-3">
                <span className="text-3xl font-black text-slate-900">{pl.price}</span>
                {pl.priceSuffix && <span className="text-sm text-slate-500 ml-1">{pl.priceSuffix}</span>}
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {pl.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    {f.included
                      ? <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      : <X size={16} className="text-slate-300 mt-0.5 flex-shrink-0" />}
                    <span className={`${f.included ? "text-slate-700" : "text-slate-400"} ${f.bold ? "font-semibold" : ""}`}>{f.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5">
                {isCurrent ? (
                  <Button disabled className="w-full" variant="outline"><Crown size={14} className="mr-1" /> Plan actual</Button>
                ) : (
                  <Button
                    onClick={() => requestUpgrade(pl.id)}
                    disabled={!isOwner}
                    className={`w-full ${pl.id === "empresa" ? "bg-violet-600 hover:bg-violet-700" : pl.id === "pro" ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}
                    data-testid={`upgrade-to-${pl.id}`}
                  >
                    {pl.id === "free" ? "Cambiar a Free" : `Activar ${pl.label}`}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isOwner && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm text-amber-900">
          Solo el <strong>dueño de la cuenta</strong> puede cambiar el plan. Contacta a tu administrador.
        </div>
      )}

      {/* Modal de "contacto para pagar" (mientras no hay Stripe/Flow) */}
      {showContact && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowContact(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="contact-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Activar plan {PLANS.find(x => x.id === showContact)?.label}</h3>
            {showContact === "free" ? (
              <p className="text-sm text-slate-600 mb-4">
                Volverás al plan Free. Si actualmente tienes plan Empresa, tus <strong>usuarios extra
                serán desactivados</strong> (sus datos se conservan) y sólo tú quedarás activo. Podrás reactivarlos al volver a un plan superior.
              </p>
            ) : (
              <div className="text-sm text-slate-600 mb-4 space-y-3" data-testid="payment-legend">
                {emailStatus === "loading" ? (
                  <p className="text-slate-500">Registrando tu solicitud…</p>
                ) : emailStatus?.error ? (
                  <p className="text-red-600">{emailStatus.error}</p>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="font-semibold text-blue-800">📧 Te enviamos las instrucciones de pago a tu correo</p>
                      <p className="text-blue-700 mt-1 text-xs">{emailStatus?.email || user?.email}</p>
                    </div>
                    <p>
                      En el correo encontrarás los datos bancarios para realizar el depósito.
                      Una vez confirmado el pago, <strong>activaremos tu plan {PLANS.find(x => x.id === showContact)?.label}</strong> y te avisaremos.
                    </p>
                    <p className="text-xs text-slate-400">¿No te llegó? Revisa spam o escríbenos a pagos@cotflowai.cl</p>
                  </>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setShowContact(null); setEmailStatus(null); }} className={showContact !== "free" ? "flex-1" : ""} data-testid="close-contact-modal">
                {showContact === "free" ? "Cerrar" : "Entendido"}
              </Button>
              {isOwner && showContact === "free" && (
                <Button
                  onClick={() => applyPlanManual(showContact)}
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-plan-change"
                >
                  {saving ? "Aplicando…" : "Confirmar cambio a Free"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
